import React, { useEffect } from 'react';
// @ts-ignore
import styles from './DiscourseForm.module.scss';
import Text from '../../../../Components/Base/Text/Text';
import SavingSpinner from '../../../../Components/Base/Loading/SavingSpinner';
import FormControl from '../../../../Components/Form/FormControl';
import DiagnosisSelectField from '../../../../Components/Base/Diagnosis/DiagnosisSelectField';
import TextArea from '../../../../Components/Base/TextArea/TextArea';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import RemoveButton from '../../../../Components/Button/RemoveButton';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import DiscourseAttachments from '../DiscourseAttachments/DiscourseAttachments';
import {
	DiscourseValues,
	useDiscourseContext,
} from '../../Services/DiscourseContext';
import FormMultiplierRemoveButton from '../../../../Components/Form/FormMultiplierRemoveButton';
import FormMultiplier from '../../../../Components/Form/FormMultiplier';
import { isEqual, cloneDeep } from 'lodash';
import InputField from '../../../../Components/Form/components/InputField';
import Loading from '../../../../Components/Loading';

interface Props {
	patientId: string;
}

const DiscourseForm: React.FC<Props> = ({ patientId }) => {
	const {
		isDiscourseSaving,
		draftDiscourseValues,
		handleSaveDiscourse,
		activeDiscourseId,
		handleDiscardActiveDiscourse,
		setDiscourseValues,
		discourseValues,
	} = useDiscourseContext();

	if (!activeDiscourseId) {
		return <Loading />;
	}

	const defaultData = {
		text: '',
		diagnosis: undefined,
		diagnoses: [{}],
		discourseId: activeDiscourseId,
	};

	const formMethods = useForm({
		defaultValues: cloneDeep(defaultData),
	});

	const formValues = formMethods.watch();

	useEffect(() => {
		setDiscourseValues(cloneDeep(defaultData));
		formMethods.reset(cloneDeep(defaultData));
	}, [activeDiscourseId]);

	useEffect(() => {
		if (
			draftDiscourseValues?.diagnoses &&
			!draftDiscourseValues?.diagnoses?.length
		) {
			draftDiscourseValues['diagnoses'] = [{}];
		}
		formMethods.reset(draftDiscourseValues);
	}, [draftDiscourseValues]);

	useEffect(() => {
		if (!isEqual(discourseValues, formValues)) {
			setDiscourseValues(cloneDeep(formValues) as DiscourseValues);
		}
	}, [formValues]);

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				<Text
					tx={'base.patientDekurz'}
					className={styles.title}
					firstUpper={true}
				/>
				{isDiscourseSaving && <SavingSpinner />}
			</div>
			<FormWrapper formMethods={formMethods}>
				<div className={'row'}>
					<div className={'col-6 mb-2'}>
						<DiagnosisSelectField
							name='diagnosis'
							label='component.ePrescription.mainDiagnosis'
						/>
					</div>
					<div className={'col-6 mb-2'}>
						<FormMultiplier
							name='diagnoses'
							addButtonTitle='base.addAnother'
							createDefault={1}>
							<DiagnosisSelectField
								name='secondaryDiagnosis'
								label='component.ePrescription.secondaryDiagnosis'
							/>
							<FormMultiplierRemoveButton>
								<RemoveButton
									onClick={() => void 0}
									title='base.shared.remove'
								/>
							</FormMultiplierRemoveButton>
						</FormMultiplier>
					</div>
				</div>
				<div style={{ display: 'none' }}>
					<InputField name='discourseId' />
				</div>
				<FormControl name='text' hideRequired={true}>
					<TextArea rows={8} className={styles.textarea} />
				</FormControl>
				<DiscourseAttachments patientId={patientId} />
				<div className='d-flex flex-row justify-content-between align-items-center'>
					<div className={'d-flex flex-row align-items-center'}>
						<SubmitButton
							onSubmit={handleSaveDiscourse}
							title={'base.shared.save'}
							disabled={!activeDiscourseId || isDiscourseSaving}
						/>
						<div className={'ml-3'}>
							{activeDiscourseId && (
								<RemoveButton
									onClick={handleDiscardActiveDiscourse}
									title='component.discourse.deleteButton'
								/>
							)}
						</div>
					</div>
				</div>
			</FormWrapper>
		</div>
	);
};

export default DiscourseForm;
